import React from "react";
import styled from "styled-components";
import { Select, SelectOption } from "@cinesys/ui/components";
import { SPOTTER_STRINGS } from "../strings";
import { seasonOptions, weekGroups, weekLabel } from "./weeks";
import { GamebookWeekStatus } from "./service";

/**
 * Gamebook tab's season/week selector. Addison's explicit direction: this lives in ITS OWN
 * section BELOW `SpotterHeader`, not inside the header row the way the reference mock
 * (`/tmp/gamebook.html`) puts its `#season`/`#week` `<select>`s (that mock's `.controls` sits
 * inside its sticky `<header>`) — so this is a new, separate component rather than an extension
 * of `SpotterHeader.tsx`. Visually mirrors `SpotterViewPicker.tsx`'s `Vpick` bar (same card
 * treatment, same shared `Select`) since the two other Spotter tabs already establish that as
 * "the picker bar under the header" pattern — but a distinct component, not a reuse of
 * `SpotterViewPicker` itself, since that component's option shape (`fieldCount`, a single value)
 * doesn't fit two independent selects side by side.
 */
const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  background: var(--spotterSurface);
  border: 1px solid var(--spotterLine);
  border-radius: 11px;
  padding: 13px 15px;
  margin-bottom: 16px;
  box-shadow: var(--spotterShadow);
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 12px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;
  flex-shrink: 0;
  cursor: default;
`;

const SelectWrap = styled.div`
  min-width: 150px;
`;

const WeekSelectWrap = styled.div`
  min-width: 220px;
`;

const ClubBadge = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ClubCode = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.04em;
  color: var(--spotterSlate);
  background: var(--spotterAccentBright);
  padding: 4px 8px;
  border-radius: 8px;
`;

const ClubName = styled.span`
  font-size: 13px;
  color: var(--spotterInkSoft);
`;

/** Right-aligned `Select` `extra` text for one week's option — the opponent (real once generated,
 *  schedule-preview otherwise) plus a checkmark ONLY once the gamebook has actually been
 *  generated, mirroring the reference mock's own `weekOption()` (` · vs/@ Opp  ✓`). `undefined`
 *  when there's no opponent data at all for that week (bye week, or too far out for the schedule
 *  API to know yet) — `Select`'s `extra` slot simply doesn't render in that case. */
function weekOpponentTag(
  status: GamebookWeekStatus | undefined
): string | undefined {
  if (!status || !status.opponentCode) {
    return undefined;
  }
  const prefix = status.home ? "vs" : "@";
  return `${prefix} ${status.opponentCode}${status.generated ? " ✓" : ""}`;
}

export interface SeasonWeekPickerProps {
  season: number;
  week: string;
  currentSeason: number;
  onSeasonChange: (season: number) => void;
  onWeekChange: (week: string) => void;
  clubCode?: string | null;
  clubName?: string | null;
  /** Per-week generated/opponent status for the current season (`useGamebookWeeks`) — `undefined`
   *  while loading, in which case week options render without the opponent/checkmark tag. */
  weekStatus?: GamebookWeekStatus[];
  /** Local portal host for the open menu — same convention `SpotterViewPicker` uses. */
  portalTarget: HTMLElement | null;
}

const SeasonWeekPicker = ({
  season,
  week,
  currentSeason,
  onSeasonChange,
  onWeekChange,
  clubCode,
  clubName,
  weekStatus,
  portalTarget,
}: SeasonWeekPickerProps) => {
  const seasonOpts: SelectOption[] = seasonOptions(currentSeason).map((s) => ({
    value: String(s),
    label: SPOTTER_STRINGS.gamebook.seasonUnit(s),
  }));
  const statusByWeek = new Map((weekStatus ?? []).map((s) => [s.week, s]));
  const weekOpts: SelectOption[] = weekGroups().flatMap((group) =>
    group.weeks.map((w) => ({
      value: w,
      label: weekLabel(w),
      group: group.label,
      extra: weekOpponentTag(statusByWeek.get(w)),
    }))
  );

  return (
    <Bar>
      <Field>
        <Label>{SPOTTER_STRINGS.gamebook.seasonLabel}</Label>
        <SelectWrap>
          <Select
            options={seasonOpts}
            value={String(season)}
            onChange={(opt) => {
              const v = (opt as SelectOption)?.value;
              if (v) {
                onSeasonChange(parseInt(v, 10));
              }
            }}
            hideClear
            portalTarget={portalTarget}
            noPadding
          />
        </SelectWrap>
      </Field>
      <Field>
        <Label>{SPOTTER_STRINGS.gamebook.weekLabel}</Label>
        <WeekSelectWrap>
          <Select
            options={weekOpts}
            value={week}
            onChange={(opt) => {
              const v = (opt as SelectOption)?.value;
              if (v) {
                onWeekChange(v);
              }
            }}
            isSearchable
            hideClear
            portalTarget={portalTarget}
            noPadding
            maxMenuHeight={420}
          />
        </WeekSelectWrap>
      </Field>
      {clubCode && (
        <ClubBadge>
          <ClubCode>{clubCode}</ClubCode>
          {clubName && <ClubName>{clubName}</ClubName>}
        </ClubBadge>
      )}
    </Bar>
  );
};

export default SeasonWeekPicker;
